import { apiGet, apiPost, apiPut, apiDownload, RequestMethod, apiRequest, apiDelete } from '.';
import {
  BareGroupBooking,
  NewGroupBooking,
  GroupBooking,
  BareVenueRequest,
  VenueRequest,
  VenueRequestComment,
  VenueRequestFile,
  BareSimpleGroupBooking,
} from '@go-shared/types/groupBooking';
import { GroupReportRequest } from '@go-shared/types/zod/groupBooking';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Property } from '@go-shared/types/property';
import { User } from '@go-shared/types/user';
import { safeIdCompare } from '@go-shared/lib/utils';
import { GroupResource } from '@go-shared/types/zod/groupResource';

// WARNING: Modifies passed `groupBooking`
function formatGroupBooking(booking: BareGroupBooking): BareGroupBooking {
  booking.date = new Date(String(booking.date));

  booking.requests.forEach(r => {
    r.date = new Date(String(r.date));
    r.comments.forEach(c => (c.date = new Date(String(c.date))));
  });

  return booking;
}

export async function getGroupBookingsByProperty(
  propertyId: string,
  since?: string,
  until?: string
): Promise<BareGroupBooking[]> {
  let queryParams: any = {};
  if (since) queryParams.from = since;
  if (until) queryParams.to = until;

  let bareGroupBookings = await apiGet<BareGroupBooking[]>(
    `/property/${propertyId}/group-booking`,
    queryParams
  );
  return bareGroupBookings.map(formatGroupBooking);
}

export async function getSimpleGroupBookingsByProperty(
  propertyId: string
): Promise<BareSimpleGroupBooking[]> {
  return apiGet<BareSimpleGroupBooking[]>(`/property/${propertyId}/group-booking-simple`);
}

export async function getGroupBooking(groupBookingId: string): Promise<BareGroupBooking> {
  let bareGroupBooking = await apiGet<BareGroupBooking>(`/group-booking/${groupBookingId}`);
  return formatGroupBooking(bareGroupBooking);
}

/**
 * Changes the `archived` attribute of a group booking
 */
export async function setArchived(
  bookingId: string,
  archived: boolean
): Promise<BareSimpleGroupBooking> {
  return apiPut(`/group-booking/${bookingId}/archive`, { archived });
}

export async function create(
  groupBooking: NewGroupBooking,
  propertyId: string
): Promise<BareGroupBooking> {
  return apiPost(`/property/${propertyId}/group-booking`, groupBooking);
}

export async function update(groupBooking: BareGroupBooking): Promise<BareGroupBooking> {
  const { _id, property } = groupBooking;
  return apiPut(`/property/${property}/group-booking/${_id}`, groupBooking);
}

export async function uploadGroupFile(
  file: File,
  groupBookingId?: string
): Promise<VenueRequestFile> {
  const fd = new FormData();
  fd.append('file', file);

  const url = groupBookingId ? `/group-booking/${groupBookingId}/file` : '/group-booking/file';

  return apiRequest(RequestMethod.Post, url, true, null, fd, {
    'Content-Type': 'multipart/form-data',
  });
}

export async function deleteGroupFile(
  fileId: string,
  groupBookingId: string
): Promise<VenueRequestFile> {
  return apiDelete(`/group-booking/${groupBookingId}/file/${fileId}`);
}

export async function uploadSubgroupFile(
  file: File,
  groupBookingId?: string,
  subgroupId?: string
): Promise<VenueRequestFile> {
  const fd = new FormData();
  fd.append('file', file);

  const url =
    groupBookingId && subgroupId
      ? `/group-booking/${groupBookingId}/subgroup/${subgroupId}/file`
      : '/group-booking/file';

  return apiRequest(RequestMethod.Post, url, true, null, fd, {
    'Content-Type': 'multipart/form-data',
  });
}

export async function deleteSubgroupFile(
  fileId: string,
  groupBookingId: string,
  subgroupId: string
): Promise<VenueRequestFile> {
  return apiDelete(`/group-booking/${groupBookingId}/subgroup/${subgroupId}/file/${fileId}`);
}

export async function addComment(
  groupBooking: BareGroupBooking,
  venueRequest: BareVenueRequest,
  comment: VenueRequestComment
): Promise<BareGroupBooking> {
  return apiPost(`/group-booking/${groupBooking._id}/request/${venueRequest._id}/comment`, comment);
}

export async function uploadBookingFile(
  file: File,
  groupBookingId?: string,
  venueRequestId?: string
): Promise<VenueRequestFile> {
  const fd = new FormData();
  fd.append('file', file);

  const url =
    groupBookingId && venueRequestId
      ? `/group-booking/${groupBookingId}/request/${venueRequestId}/file`
      : '/group-booking/file';

  return apiRequest(RequestMethod.Post, url, true, null, fd, {
    'Content-Type': 'multipart/form-data',
  });
}

export async function deleteBookingFile(
  groupBookingId: string,
  venueRequestId: string,
  fileId: string
): Promise<VenueRequestFile> {
  return apiDelete(`/group-booking/${groupBookingId}/request/${venueRequestId}/file/${fileId}`);
}

export async function exportGroupBookings(propertyId: string) {
  return apiDownload(`/property/${propertyId}/group-booking/export`);
}

export async function exportGroups(propertyId: string) {
  return apiDownload(`/property/${propertyId}/groups/export`);
}

export async function exportGroupBookingsBrand() {
  return apiDownload(`/group-booking/export`);
}

export async function exportGroupMemo(
  groupBookingId: string,
  approvedOnly: boolean,
  from?: string,
  to?: string
): Promise<string> {
  let queryParams: any = { approvedOnly };
  if (from) queryParams.from = from;
  if (to) queryParams.to = to;

  return apiPost(`/group-booking-memo/${groupBookingId}/export`, undefined, queryParams);
}

export async function exportGroupReportCSV(
  propertyId: string,
  requests: GroupReportRequest[],
  approvedOnly: boolean
): Promise<void> {
  let queryParams: any = { approvedOnly };

  return apiDownload(
    `/property/${propertyId}/group-booking-report-csv/export`,
    RequestMethod.Post,
    requests,
    undefined,
    queryParams
  );
}

export function populate(
  bareGroupBooking: BareGroupBooking,
  property: Property,
  users: User[],
  venueTemplates: VenueTemplate[],
  groupResources: GroupResource[]
): GroupBooking {
  const createdBy = users.find(u => safeIdCompare(u._id, bareGroupBooking.createdBy));
  if (!createdBy) throw new Error(`Could not find user ${bareGroupBooking.createdBy}`);

  return {
    ...bareGroupBooking,
    property,
    createdBy,
    requests: bareGroupBooking.requests.map(r =>
      populateVenueRequest(r, users, venueTemplates, bareGroupBooking, groupResources)
    ),
  };
}

export function populateVenueRequest(
  bareRequest: BareVenueRequest,
  users: User[],
  venueTemplates: VenueTemplate[],
  booking: BareGroupBooking | GroupBooking,
  groupResources: GroupResource[]
): VenueRequest {
  const venue = venueTemplates.find(vt => vt._id === bareRequest.venue);
  const createdBy = users.find(u => safeIdCompare(u._id, bareRequest.createdBy));

  if (!venue) throw new Error(`Could not find venue ${bareRequest.venue}`);
  if (!createdBy) throw new Error(`Could not find user ${bareRequest.createdBy}`);

  return {
    ...bareRequest,
    venue,
    createdBy,
    subgroup: booking.subgroups.find(sg => safeIdCompare(sg._id, bareRequest.subgroup)),
    venueConfig: venue.configurations.find(ct => safeIdCompare(ct._id, bareRequest.venueConfig)),
    resources: bareRequest.resources.map(r => {
      const resourceDetails = groupResources.find(gr => safeIdCompare(gr._id, r.resourceId));
      return {
        ...r,
        name: resourceDetails?.archived
          ? `${resourceDetails?.name || r.name} (archived)`
          : resourceDetails?.name || r.name,
        price: resourceDetails?.price ?? r.price,
        resourceType: resourceDetails?.resourceType || r.resourceType,
        archived: resourceDetails?.archived || r.archived,
      };
    }),
  };
}

export function bare(groupBooking: GroupBooking): BareGroupBooking {
  return {
    ...groupBooking,
    _id: groupBooking._id,
    createdBy: groupBooking.createdBy._id,
    requests: groupBooking.requests.map(r => bareVenueRequest(r)),
    property: groupBooking.property._id,
  };
}

export function bareVenueRequest(venueRequest: VenueRequest): BareVenueRequest {
  return {
    ...venueRequest,
    venue: venueRequest.venue._id,
    createdBy: venueRequest.createdBy._id,
    subgroup: venueRequest.subgroup?._id,
    venueConfig: venueRequest.venueConfig?._id,
  };
}

interface SerializedBuffer {
  type: 'Buffer';
  data: number[];
}

export async function exportLetter(
  propertyId: string,
  groupBookingId: string,
  letter: string,
  approvedOnly: boolean = true,
  fromDate?: string,
  toDate?: string
): Promise<SerializedBuffer> {
  const params = {
    approvedOnly: String(approvedOnly),
    fromDate,
    toDate,
  };

  return apiGet<SerializedBuffer>(
    // eslint-disable-next-line max-len
    `/ext/carnival-group-letters/property/${propertyId}/group-booking/${groupBookingId}/${letter}/export`,
    params
  );
}
